import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  Campaign,
  CAMPAIGN_ID,
  CampaignBanner,
  CampaignBannerItem,
  CampaignContent,
  DAYPART,
  isCombo,
  PricedCampaignBanner,
  PricedCampaignBannerItem,
} from '@core/models';
import { LtoPricingFormatted, LtoPricingRequestCombo, LtoPricingRequestItem } from '@core/models/lto-pricing.model';
import { LaunchDarklyService } from '@core/services/launchdarkly.service';
import { LtoPricingService } from '@shared/services/lto-pricing.service';
import { combineLatest, debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pos-content',
  templateUrl: './pos-content.component.html',
  styleUrls: ['./pos-content.component.scss'],
  standalone: false,
})
export class POSContentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() daypart: string = DAYPART.full_menu;
  @Input() contentId: string = '';

  public campaignId: CAMPAIGN_ID = CAMPAIGN_ID.national;
  public content: CampaignContent | undefined;

  public pricedBanners: PricedCampaignBanner[] = [];

  private destroy$ = new Subject<void>();
  private currentCampaigns: Campaign[] = [];
  private currentLtoPricing: LtoPricingFormatted | null = null;

  constructor(
    private ltoPricingService: LtoPricingService,
    private ldService: LaunchDarklyService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {
    combineLatest([this.ldService.currentMenuContent(), this.ltoPricingService.pricingData$])
      .pipe(debounceTime(30), takeUntil(this.destroy$))
      .subscribe(([campaigns, pricingData]) => {
        this.zone.run(() => {
          this.currentCampaigns = campaigns;
          this.currentLtoPricing = pricingData;
          this.render();
        });
      });
  }

  ngOnInit(): void {
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.render();
    }
  }

  private render(): void {
    this.campaignId = CAMPAIGN_ID.national;
    this.parsePOSPricingData(this.currentCampaigns, this.currentLtoPricing);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Helper function to get the price of an LTO item or combo. If the id for the item
   * isn't found, or the price doesn't exist (i.e. 0), we will simply return a blank string.
   * @param id - The ID of the LTO item or combo we want to get the price of.
   * @param type - Whether the LTO item is a combo or not.
   * @param pricingData - The LTO pricing data we want to search in.
   * @returns - A string with the price of the LTO item or combo.
   */
  public getItemPrice(
    id: string,
    type: 'combos' | 'items',
    pricingData: LtoPricingFormatted | null,
  ): number | undefined {
    if (pricingData) {
      const pricingObject = pricingData[type];
      if (pricingObject && pricingObject[id] !== undefined) {
        return pricingObject[id];
      }
    }
    return undefined;
  }

  /**
   * Helper function to get the price info for an LTO item or combo from a banner's POS. Checks whether or not
   * the banner's POS is a combo or not and then calls getItemPrice() with the appropriate type and ID.
   * @param bannerPOS - The POS object from a banner.
   * @param pricingData - The LTO pricing data we want to search in.
   * @returns - A string with the price of the LTO item or combo.
   */
  public getBannerItemPOSInfo(
    bannerPOS: LtoPricingRequestItem | LtoPricingRequestCombo,
    pricingData: LtoPricingFormatted | null,
  ): number | undefined {
    if (isCombo(bannerPOS)) {
      return this.getItemPrice((bannerPOS as LtoPricingRequestCombo).promoId, 'combos', pricingData);
    } else {
      return this.getItemPrice((bannerPOS as LtoPricingRequestItem).posItemId, 'items', pricingData);
    }
  }

  /**
   * Function to combine the LaunchDarkly menu campaigns with the latest pricing data.
   * We will populate our pricedBanners array with the banners for the relevant campaign,
   * daypart, and id for our inputted POS options.
   * @param menuCampaigns - Current LD menu campaigns array
   * @param pricingData - Data from the most recently emitted pricing data
   */
  public parsePOSPricingData(menuCampaigns: Campaign[], pricingData: LtoPricingFormatted | null) {
    if (!menuCampaigns || !menuCampaigns.length) {
      return;
    }
    const daypart = !this.daypart || this.daypart === 'rod' ? DAYPART.full_menu : this.daypart;
    const menuContent = this.ltoPricingService.getCampaignContent(
      menuCampaigns,
      this.campaignId,
      daypart,
      this.contentId,
    );

    const newPricedBanners: PricedCampaignBanner[] = [];
    menuContent?.banners.forEach((banner: CampaignBanner) => {
      const pricedBanner: PricedCampaignBanner = {
        id: banner.id,
        imageUrl: banner.imageUrl,
        items: [],
      };
      banner.items.forEach((item: CampaignBannerItem) => {
        const posPrice = this.getBannerItemPOSInfo(item.pos, pricingData);
        // Only trigger contingency fallback if the item has price coordinate overlays.
        // Pure graphical marketing banners (!item.coords) retain their variant artwork.
        if (posPrice !== 0 || !item.coords) {
          const pricedItem: PricedCampaignBannerItem = {
            price: posPrice ? '' + posPrice : '',
            coords: item.coords,
          };

          pricedBanner.items.push(pricedItem);
        } else {
          pricedBanner.imageUrl = banner.fallbackImageUrl;
          pricedBanner.fallbackEnabled = true;
        }
      });
      newPricedBanners.push(pricedBanner);
    });

    if (JSON.stringify(this.pricedBanners) !== JSON.stringify(newPricedBanners)) {
      this.content = menuContent;
      this.pricedBanners = newPricedBanners;
      this.cdr.markForCheck();
    }
  }
}
